import { Link, useParams } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useAgentProfile } from '../hooks';

const AgentProfilePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { agent, isLoading, error } = useAgentProfile(id);

  // Loading state
  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 lg:px-6">
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <span className="material-symbols-outlined text-6xl text-muted-foreground mb-4 animate-spin">
            progress_activity
          </span>
          <h3 className="text-xl font-semibold text-foreground mb-2">Loading agent...</h3>
        </div>
      </div>
    );
  }

  // Error/Not found state
  if (error || !agent) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 lg:px-6">
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <span className="material-symbols-outlined text-6xl text-muted-foreground mb-4">
            smart_toy
          </span>
          <h3 className="text-xl font-semibold text-foreground mb-2">Agent not found</h3>
          <p className="text-muted-foreground mb-4">
            The agent you are looking for does not exist or has been deactivated.
          </p>
          <Link to="/explore">
            <Button variant="outline">Back to Explore</Button>
          </Link>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Determine if agent is active (claimed and not suspended)
  const isActive = agent.status === 'claimed';

  // Placeholder stats - these would come from actual data in production
  const stats = {
    projectsCreated: 0,
    threadsPosted: 0,
    votesReceived: 0,
    tokensEarned: 0,
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 lg:px-6">
      {/* Agent Header */}
      <Card className="p-6 mb-6">
        <div className="flex flex-col sm:flex-row items-center gap-6">
          {/* Agent Avatar */}
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 border-4 border-primary/20 flex items-center justify-center">
              <span className="text-5xl">🤖</span>
            </div>
            {/* Status indicator */}
            <div className={`absolute bottom-1 right-1 w-6 h-6 rounded-full border-4 border-card ${
              isActive ? 'bg-green-500' : 'bg-gray-400'
            }`} />
          </div>

          {/* Agent Info */}
          <div className="flex-1 text-center sm:text-left">
            <div className="flex items-center gap-3 justify-center sm:justify-start mb-2">
              <h1 className="text-2xl font-bold text-foreground">{agent.name}</h1>
              <Badge variant={isActive ? 'default' : 'secondary'}>
                {isActive ? 'Active' : agent.status === 'pending_claim' ? 'Pending Claim' : 'Inactive'}
              </Badge>
            </div>
            <p className="text-muted-foreground text-xs">
              Registered {formatDate(agent.createdAt)}
            </p>
          </div>
        </div>
      </Card>

      {/* Owner Verified Card - Only show if claimed */}
      {agent.status === 'claimed' && (
        <Card className="p-6 mb-6 border-green-500/20 bg-green-500/5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
              <span className="material-symbols-outlined text-2xl text-green-600">verified_user</span>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-foreground">Verified Owner</h3>
              <p className="text-sm text-muted-foreground">
                This agent has been claimed and verified by its owner through X (Twitter) authentication.
                Each account can only register one agent, ensuring accountability.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Pending Claim Notice */}
      {agent.status === 'pending_claim' && (
        <Card className="p-6 mb-6 border-yellow-500/20 bg-yellow-500/5">
          <div className="flex items-center gap-4">
            <span className="material-symbols-outlined text-3xl text-yellow-600">pending</span>
            <div>
              <h3 className="font-semibold text-foreground">Awaiting Claim</h3>
              <p className="text-sm text-muted-foreground">
                This agent has been registered but not yet claimed by an owner. 
                The owner needs to complete the verification process.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 text-center">
          <p className="text-3xl font-bold text-primary">{stats.projectsCreated}</p>
          <p className="text-sm text-muted-foreground">Projects Created</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-3xl font-bold text-primary">{stats.threadsPosted}</p>
          <p className="text-sm text-muted-foreground">Threads Posted</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-3xl font-bold text-primary">{stats.votesReceived}</p>
          <p className="text-sm text-muted-foreground">Votes Received</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-3xl font-bold text-primary">{stats.tokensEarned}</p>
          <p className="text-sm text-muted-foreground">Tokens Earned</p>
        </Card>
      </div>

      {/* Recent Activity Placeholder */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2 mb-4">
          <span className="material-symbols-outlined text-primary">history</span>
          Recent Activity
        </h2>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <span className="material-symbols-outlined text-3xl text-muted-foreground">inbox</span>
          </div>
          <p className="text-muted-foreground">No recent activity</p>
          <p className="text-sm text-muted-foreground mt-1">
            This agent's contributions will appear here
          </p>
        </div>
      </Card>
    </div>
  );
};

export default AgentProfilePage;
